import { useIsMobile } from "../../hooks/use-mobile";
import { Icon } from "@iconify/react";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  Locate,
  MapPinIcon,
  Phone,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { useMemo } from "react";

export const Footer = ({ phoneNumber }: { phoneNumber: string }) => {
  const [isMobile] = useIsMobile();
  const { t } = useLanguage();

  const navItems = useMemo(
    () => [
      { name: t("trangchu"), href: "/" },
      { name: t("phongkham"), href: "/phong-kham" },
      { name: t("bacsi"), href: "/bac-si" },
      { name: t("tracuuphieukham"), href: "/phieu-kham" },
    ],
    [t],
  );

  const useNavItems = () => {
    const { pathname } = useLocation();

    const navItemsWithActive = navItems.map((item) => ({
      ...item,
      isActive:
        item.href !== "#" &&
        (pathname === item.href || pathname.startsWith(item.href + "/")),
    }));

    return navItemsWithActive;
  };

  const navItemsActive = useNavItems();

  return (
    <footer
      className={`bg-sub-primary border-t border-amber-700 ${isMobile && "pb-20"}`}
    >
      <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-8 pt-10 pb-4 space-y-10">
        {/* Hàng trên: logo + câu mời gọi */}
        <div className="flex flex-col gap-4 items-start justify-between md:flex-row md:items-center">
          <div className="flex items-center gap-2 h-24">
            {/* Logo Khoa */}
            <img
              src="/images/logo_108.svg"
              alt="Khu khám Đối ngoại-Quốc tế"
              className="h-full w-auto"
            />
            <img
              src="/images/text_a16.png"
              alt="Bệnh viện Trung Ương Quân Đội 108"
              className={`w-auto object-contain ${isMobile ? 'h-[50%]' : 'h-[60%]'}`}
            />
          </div>

          <div className="text-sm md:text-base text-green-700 max-w-xl">
            <p className="md:text-base text-sm text-black">{t("motachao")}</p>
          </div>
        </div>

        {/* Hàng giữa: Liên hệ + Bản đồ */}
        <div className="grid gap-10 lg:grid-cols-[1.4fr_0.8fr_1.4fr]">
          {/* Cột trái: Liên hệ với chúng tôi */}
          <div>
            <h3 className="header-font text-base md:text-lg font-semibold uppercase text-amber-900 mb-4">
              {t("lienhevoichungtoi")}
            </h3>

            <div className="space-y-3 text-sm md:text-base">
              <div className="flex items-start gap-3">
                <MapPinIcon className="w-4 h-4 md:w-5 md:h-5 shrink-0" />
                <p>{t("diachichitiet")}</p>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 md:w-5 md:h-5" />
                <p>
                  Hotline: <strong>{phoneNumber}</strong>
                </p>
              </div>
            </div>

            <a
              href="https://www.facebook.com/khoaquoctebv108"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="inline-flex mt-6 items-center justify-center group"
            >
              <Icon
                icon="mdi:facebook"
                className="h-8 w-8 text-black transition-opacity duration-200 group-hover:opacity-60"
              />
            </a>
          </div>

          <nav>
            <h3 className="header-font text-base md:text-lg font-semibold uppercase text-amber-900 mb-4">
              {t("dieuhuong")}
            </h3>

            <ul className="space-y-3 text-sm md:text-base">
              {navItemsActive.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className={`group flex items-center gap-3 hover:text-green-600
                      ${item.isActive && "text-green-600"}`}
                  >
                    {/* mũi tên nhỏ bên trái, giống screenshot */}
                    <ChevronRight className="group-hover:text-green-600 w-4 h-4" />

                    <span className="relative">
                      {item.name}
                      {/* gạch xanh dưới item khi hover */}
                      <span
                        className={`absolute left-0 -bottom-1 h-[2px] w-0 bg-emerald-500 transition-all duration-300 group-hover:w-full
                        ${item.isActive && "w-full"}`}
                      />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Cột phải: Bản đồ */}
          <div>
            <h3 className="header-font text-base md:text-lg font-semibold uppercase text-amber-900 mb-4">
              {t("bandoview")}
            </h3>
            <div className="bg-white rounded-2xl overflow-hidden shadow-md h-[300px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3724.3568504630052!2d105.8611476!3d21.018402899999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3135abee2cabbe6f%3A0xa87d18d26d9dc536!2zQuG7h25oIHZp4buHbiBUcnVuZyDGsMahbmcgUXXDom4gxJHhu5lpIDEwOA!5e0!3m2!1svi!2sus!4v1773730808777!5m2!1svi!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t("bandoview")}
              ></iframe>
            </div>
          </div>
        </div>

        <div className="border-t border-amber-600 pt-4 flex flex-col gap-2 text-xs md:text-base text-amber-800 md:flex-row md:items-center md:justify-center">
          <p className="text-center">
            © {new Date().getFullYear()} {t("copyright_bv")}
          </p>
        </div>
      </div>
    </footer>
  );
};
